export enum CompanyStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  DELETED = "deleted",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  TRIAL = "trial",
}

export interface PaymentRecord {
  id: string;
  purchasedLimit: number;
  amountPaid: number;
  currency: string;
  startDate: string;
  expiryDate: string;
  type: "trial" | "premium";
  paymentPlan?: "monthly" | "annually" | "trial";
  durationDays: number;
  couponCode?: string;
  paymentStatus: "pending" | "paid" | "failed";
  description?: string;
  paidAt?: string;
  iyzicoSubscriptionReferenceCode?: string;
  // Parasut entegrasyonu için eklenen alanlar
  parasutInvoiceId?: string;
  parasutEInvoiceId?: string;
  parasutEArchiveId?: string;
}

export interface CompanyPaymentRecord {
  id: string;
  companyName: string;
  companyId: number;
  planType: string;
  amount: number;
  currency: string;
  paymentStatus: string;
  paymentDate: string;
  expiryDate: string;
  subscriptionType: string;
  userLimit: number;
  description?: string;
}

export interface CompanySettings {
  maxDailyWorkHours: number;
  lateTolerance: boolean;
  lateTolerationMinutes: number;
  ipControl: boolean;
  annualLeaveLimit: number;
  shiftMode: "assignment" | "fixed" | "both";
  useFixedShift: boolean;
  fixedShiftDays: string[];
  fixedShiftStartTime: string;
  fixedShiftEndTime: string;
  sendMailLeave: boolean;
  sendMailTicket: boolean;
  sendMailExcuse: boolean;
  sendNotifLeave: boolean;
  sendNotifTicket: boolean;
  sendNotifExcuse: boolean;
  sendNotifBreak: boolean;
  sendSMSLeave: boolean;
  sendSMSTicket: boolean;
  sendSMSExcuse: boolean;
  sendSMSBreak: boolean;
  defaultBreakDuration: number;
  maxBreakDuration: number;
  userLimit: number;
  smsLimit?: number;
  workOnNewYearsDay?: boolean;
  workOnLabourDay?: boolean;
  workOnCommemorationOfAtaturkDay?: boolean;
  workOnDemocracyAndNationalUnityDay?: boolean;
  workOnVictoryDay?: boolean;
  workOnRepublicDay?: boolean;
  ramadanHolidayDuration?: number;
  eidAlAdhaHolidayDuration?: number;
  ramadanHolidayDates?: string[];
  eidAlAdhaHolidayDates?: string[];
  customHolidayDates?: string[]; // Deprecated: Use customHolidays instead
  customHolidays?: CustomHoliday[];
  nationalHolidayDates?: string[];
}

// Özel tatil günü interface'i
export interface CustomHoliday {
  date: string; // "YYYY-MM-DD"
  description: string;
  isFullDay: boolean;
  startTime?: string; // "HH:mm", optional
  endTime?: string; // "HH:mm", optional
}

export interface CompanySubscription {
  id: number;
  status: string;
  startDate: string;
  endDate: string;
  type: string;
  companyId: number;
}

// Oluşturan süper admin bilgisi için interface
export interface CreatedByUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string | null;
  role: string;
  title?: string;
  hireDate?: string;
  workDuration?: number;
  status: string;
  employeeType?: string;
  createdAt?: string;
  updatedAt?: string;
  name?: string; // Fallback için
}

export interface Company {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  city?: string;
  district?: string;
  zipCode?: string;
  taxNumber: string;
  taxOffice?: string;
  iyzicoCustomerReferenceCode?: string;
  subscriptionStatus: SubscriptionStatusEnum;
  gracePeriodEndsAt: Date | null;
  activeSubscriptionExpiresAt: Date | null;
  subscriptionReferenceCode: string | null;
  subscriptionType: "monthly" | "annually" | null;
  settings?: CompanySettings;
  paymentRecords?: any[];
  status: CompanyStatus;
  users?: any[];
  branches?: any[];
  departments?: any[];
  isDeleted: boolean;
  onboardingSecret?: string;
  onboardingExpiresAt?: Date;
  onboardingUsed: boolean;
  createdAt: Date;
  updatedAt: Date;
  billingAddress?: BillingAddress;
  subscription?: CompanySubscription;
  createdBy?: CreatedByUser; // Backend'den gelen createdBy objesi
  createdBySuperAdmin?: boolean; // Süper admin tarafından oluşturuldu mu?
  createdBySuperAdminName?: string; // Oluşturan süper adminin adı
}

export interface SubscriptionDetails {
  lastPaymentDate: string | Date | null;
  expiryDate: string | Date | null;
  lastPackageDescription: string;
  gracePeriodEndsAt?: string | Date | null;
}

export interface CompanyDetailsForSuperAdmin {
  id: number;
  name: string;
  email: string;
  phone: string;
  createdAt: Date;
  status: CompanyStatus;
  subscriptionStatus: string;
  userLimit: number;
  currentUserCount: number;
  branchCount: number;
  departmentCount: number;
  activeSubscriptionExpiresAt?: string | Date | null;
  gracePeriodEndsAt?: string | Date | null;
  subscriptionDetails: SubscriptionDetails;
}

export interface BillingAddress {
  alias: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  contactName?: string;
  taxNumber?: string;
  taxOffice?: string;
}

export interface CompanySubscriptionStatusInfo {
  expired: boolean;
  type: "trial" | "premium" | null;
  daysLeft: number;
  userLimit: number;
  message: string;
}

export interface TopPayingCompany {
  companyName: string;
  totalPaid: number;
  lastPayment: string;
  planType: string;
  transactionCount: number;
}

export interface PaymentStats {
  totalRevenue: number;
  monthlyRevenue: number;
  totalTransactions: number;
  activeSubscriptions: number;
  trialUsers: number;
  canceledSubscriptions: number;
  averageRevenuePerUser: number;
  averageOrderValue?: number;
  revenueGrowth: number;
  topPayingCompanies: TopPayingCompany[];
  monthlyRevenueData: Array<{
    month: string;
    revenue: number;
    transactions: number;
  }>;
  paymentStatusDistribution: {
    paid: number;
    pending: number;
    failed: number;
    refunded: number;
  };
  planTypeDistribution: {
    premium: number;
    basic: number;
    enterprise: number;
  };
}

export interface PaymentFilters {
  paymentStatus?: string;
  planType?: string;
  dateRange?: [string, string];
  companyName?: string;
}

export interface UpdateCompanySettingsDTO {
  settings: CompanySettings;
}

export interface AnnualLeaveLimitResponse {
  annualLeaveLimit: number;
}

export interface CreateCompanyDto {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  city?: string;
  district?: string;
  taxNumber: string;
  taxOffice: string;
  defaultBreakDuration: number;
  status: CompanyStatus;
  settings?: CompanySettings;
  referralCode?: string; // Referans kodu (yeni şirket kaydı için)
}

export interface UpdateCompanyDto {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  district?: string;
  zipCode?: string;
  taxNumber?: string;
  taxOffice?: string;
  status?: CompanyStatus;
  settings?: Partial<CompanySettings>;
  subscriptionStatus?: string;
  userLimit?: number;
  activeSubscriptionExpiresAt?: string | Date;
}

export enum SubscriptionStatusEnum {
  ACTIVE = "active",
  INACTIVE = "inactive",
  IN_GRACE_PERIOD = "in_grace_period",
  EXPIRED = "expired",
  TRIAL = "trial",
}

export interface UpdateSubscriptionDto {
  userLimit?: number;
  subscriptionStatus?: SubscriptionStatusEnum;
  activeSubscriptionExpiresAt?: Date | string;
  gracePeriodEndsAt?: Date | string | null;
}

export interface SuperSettings {
  id: number;
  name: string;
  settings: CompanySettings;
}

export interface AboutUs {
  content: string;
}

// Setup Todo için type'lar
export interface CompanySetupStatus {
  hasBranches: boolean;
  hasDepartments: boolean;
  hasEmployees: boolean;
}

export interface SetupTodoItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  route: string;
  icon: string;
  priority: number;
}
